/* eslint-disable no-undef */
import React, { useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Link,
  Typography,
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Logo } from '@horwath-htl/shared-branding';
import { backupCart } from '../utils/cartPersistence';
import AuthMethodSelector from '../auth/components/AuthMethodSelector';
import { Home as HomeIcon } from '@mui/icons-material';
import { SEO } from '../components/SEO';

export const LoginPage: React.FC = () => {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for Microsoft authentication
    if (accounts.length > 0) {
      // Get the return path from location state, or default to dashboard
      const from = (location.state as any)?.from || '/dashboard';
      navigate(from);
      return;
    }

    // Check for OTP authentication
    const accessToken = sessionStorage.getItem('access_token');
    if (accessToken) {
      // Verify token is not expired
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          const from = (location.state as any)?.from || '/dashboard';
          navigate(from);
        }
      } catch (e) {
        // Invalid token, continue to login page
      }
    }
  }, [accounts, navigate, location]);

  const handleAuthSuccess = () => {
    // Backup cart before navigation
    backupCart();
    
    // Get the return path from location state, or default to dashboard
    const from = (location.state as any)?.from || '/dashboard';
    
    // Store the return path in session storage
    sessionStorage.setItem('loginReturnPath', from);
    
    // Navigate to the return path
    navigate(from);
  };

  return (
    <>
      <SEO
        title="Login"
        description="Sign in to access your HTL MarketCompass account"
        robots="noindex, nofollow"
      />
      <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card sx={{ textAlign: 'center', p: 4 }}>
        <CardContent>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Logo height={60} />
            </Box>
            <Link
              href="/"
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 0.5, 
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline'
                }
              }}
            >
              <HomeIcon fontSize="small" />
              <Typography variant="body2">Back to Home</Typography>
            </Link>
          </Box>

          <AuthMethodSelector 
            onSuccess={handleAuthSuccess}
            redirectPath={(location.state as any)?.from || '/dashboard'}
          />
        </CardContent>
      </Card>
      </Container>
    </>
  );
};
